//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { TriggerAttributionMessage } from 'components/TriggerAttributionMessage';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { StartCheckState, TaskStatus, useCheckApiReady } from 'hook/useCheckApiReady';
import { ControllerFwUpgradeProgress } from 'models/controller-fw-upgrade-progress';
import useControllerFwUpgradeProgress from 'pages/Topology/ControllerDrawer/useControllerFwUpgradeProgress';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { sseConnectionManager } from 'utils/sseConnectionManager';
import { createToastConfig } from 'utils/toastConfig';
import { useActionProgressFlow } from './useActionProgressFlow';
import { EventTriggerSource, useSseFlashStatus } from './useSseFlashStatus';

enum EventTriggerType {
  Restart = 1,
  Reset = 2,
  Upgrade = 3,
  FlashStatus = 4,
}

interface EventTriggerFlashStatusPayload {
  flow: 'restart' | 'upgrade';
  errorMessage: string;
}

interface EventTriggerData {
  event: EventTriggerType;
  triggerSource?: EventTriggerSource;
  triggerUser?: string;
  triggerClientId?: string;
  serialNumber?: string;
  eventPayload?: ControllerFwUpgradeProgress | EventTriggerFlashStatusPayload;
}

const RESTART_TIME = 3 * 60 * 1000;
const RESET_TIME = 3 * 60 * 1000;
const RETRY_INTERVAL_MS = 10 * 1000;
const MAX_RETRY_TIME = 120 * 1000;
const REDIRECT_DELAY_MS = 3000;
const FINISH_UPGRADE_DELAY_MS = 10000;

export const useSseEventTrigger = () => {
  const { t } = useTranslation();
  const { cbtApiUrl } = useConfig();
  const location = useLocation();
  const { logout, getCheckApiIsReady } = useAuth();
  const { addConfirmModal } = useModal();
  const { addToast } = useToast();
  const { startCheck } = useCheckApiReady();
  const { runActionProgress } = useActionProgressFlow();
  const {
    startTimer,
    stopTimer,
    onUpgradeSuccess,
    onUpgradeError,
    progressRef,
    endRadialProgressModal: endUpgradeRadialProgressModal,
    updateProgressFromEvent,
    startRebootProgress,
  } = useControllerFwUpgradeProgress();

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const upgradeStartedRef = useRef(false);
  const upgradeRebootCheckStartedRef = useRef(false);

  const { displayArchivSystemRadialProgressModal, endRadialProgressModal } = useSseFlashStatus();

  const redirectUrl = useCallback(() => {
    setTimeout(() => logout(), REDIRECT_DELAY_MS);
  }, [logout]);

  const displayRestartRadialProgress = useCallback(
    () =>
      runActionProgress({
        checkId: 'restart',
        name: 'eventTriggerControllerRestart',
        title: t('modal.restarting'),
        message: t('modal.restartingMessage', {
          minutes: Math.floor(RESTART_TIME / 1000 / 60).toString(),
        }),
        totalTime: RESTART_TIME,
        redirectDelay: REDIRECT_DELAY_MS,
      }),
    [runActionProgress, t]
  );

  const displayResetProgressModal = useCallback(
    (triggerUser?: string, triggerClientId?: string) =>
      runActionProgress({
        checkId: 'reset',
        name: 'eventTriggerControllerReset',
        title: t('modal.restting'),
        message: (
          <>
            <TriggerAttributionMessage action="reset" triggerUser={triggerUser} triggerClientId={triggerClientId} />
            <span>{t('modal.restoringMessageAvoidAnyActions')}</span>
          </>
        ),
        totalTime: RESET_TIME,
        redirectDelay: REDIRECT_DELAY_MS,
      }),
    [runActionProgress, t]
  );

  const finishUpgrade = useCallback(() => {
    upgradeStartedRef.current = false;
    upgradeRebootCheckStartedRef.current = false;
    addConfirmModal({
      open: true,
      id: 'eventTriggerControllerFinishUpgrade',
      name: 'eventTriggerControllerFinishUpgrade',
      btnOk: t('modal.btnDismiss'),
      type: t('modal.btnSuccess'),
      title: t('modal.upgradeSuccessful'),
      message: t('modal.upgradeSuccessfulMessage'),
      onClickOk: redirectUrl,
    });
    setTimeout(() => {
      redirectUrl();
    }, FINISH_UPGRADE_DELAY_MS);
  }, [addConfirmModal, redirectUrl, t]);

  const startUpgradeCheckCallbackFunction = useCallback(
    (state: StartCheckState) => {
      const status = state?.upgrade?.status as TaskStatus;
      if (status !== 'success' && status !== 'error') return;

      stopTimer();
      endUpgradeRadialProgressModal();

      if (status === 'success') {
        finishUpgrade();
        return;
      }

      upgradeStartedRef.current = false;
      upgradeRebootCheckStartedRef.current = false;
      /* 走到這裡 status 必為 'error'(success 已於上方 return) */
      addToast({
        ...createToastConfig(false, 'upgrade', t),
        duration: 30 * 1000,
      });
    },
    [addToast, endUpgradeRadialProgressModal, finishUpgrade, stopTimer, t]
  );

  const startUpgradeFlow = useCallback(
    (triggerUser?: string, triggerClientId?: string) => {
      if (upgradeStartedRef.current) return;
      upgradeStartedRef.current = true;
      onUpgradeSuccess(finishUpgrade);
      onUpgradeError(() => {
        upgradeStartedRef.current = false;
        upgradeRebootCheckStartedRef.current = false;
      });
      startTimer(triggerUser, { polling: false, triggerClientId });
    },
    [finishUpgrade, onUpgradeError, onUpgradeSuccess, startTimer]
  );

  const displayUpgradeRestartProgress = useCallback(
    async (triggerUser?: string, triggerClientId?: string) => {
      if (upgradeRebootCheckStartedRef.current) return;
      upgradeRebootCheckStartedRef.current = true;

      // 重開機期間裝置不再回報進度,改由前端把進度推進到 99% 並停住
      startRebootProgress(progressRef.current.progressValue, triggerUser, triggerClientId);

      await startCheck({
        checkId: 'upgrade',
        checkFunction: async () => getCheckApiIsReady(),
        enableRetry: true,
        retryMode: 'time',
        callbackFunction: startUpgradeCheckCallbackFunction,
        totalDuration: RESTART_TIME + MAX_RETRY_TIME,
        startCheckAt: RESTART_TIME - 10 * 1000,
        retryInterval: RETRY_INTERVAL_MS,
      });
    },
    [getCheckApiIsReady, progressRef, startCheck, startUpgradeCheckCallbackFunction, startRebootProgress]
  );

  const isControllerFwUpgradeProgress = (payload: EventTriggerData['eventPayload']): payload is ControllerFwUpgradeProgress =>
    !!payload && 'progressValue' in payload && 'progressType' in payload && 'timestamp' in payload;

  const isUpgradeRebootProgress = (payload: ControllerFwUpgradeProgress) => payload.progressType === 3 || payload.progressValue >= 90;

  const isFlashStatusPayload = (payload: EventTriggerData['eventPayload']): payload is EventTriggerFlashStatusPayload =>
    !!payload && 'flow' in payload && 'errorMessage' in payload;

  const handleFlashStatusEvent = useCallback(
    (payload: EventTriggerFlashStatusPayload, triggerUser?: string, triggerClientId?: string, triggerSource?: EventTriggerSource) => {
      if (payload.flow === 'restart') {
        /* 收尾畫面沿用該次操作的觸發者資訊(後端 sse.service 已由 context 補上),不再依賴跨 hook 實例共享的狀態 */
        endRadialProgressModal(displayRestartRadialProgress, { isRestart: true, triggerUser, triggerClientId, triggerSource });
        return;
      }

      if (payload.flow === 'upgrade') {
        displayUpgradeRestartProgress();
      }
    },
    [displayRestartRadialProgress, displayUpgradeRestartProgress, endRadialProgressModal]
  );

  const handleEventTriggerData = useCallback(
    (data: EventTriggerData) => {
      if (/^(\/sites\/\d+)?\/rtty\//.test(location.pathname)) return;

      switch (data.event) {
        case EventTriggerType.Restart:
          displayArchivSystemRadialProgressModal({
            isRestart: true,
            triggerUser: data.triggerUser,
            triggerClientId: data.triggerClientId,
            triggerSource: data.triggerSource,
          });
          break;

        case EventTriggerType.Reset:
          displayResetProgressModal(data.triggerUser, data.triggerClientId);
          break;

        case EventTriggerType.Upgrade:
          if (isControllerFwUpgradeProgress(data.eventPayload)) {
            startUpgradeFlow(data.triggerUser, data.triggerClientId);
            updateProgressFromEvent(data.eventPayload, data.triggerUser, data.triggerClientId);
            if (isUpgradeRebootProgress(data.eventPayload)) {
              displayUpgradeRestartProgress(data.triggerUser, data.triggerClientId);
            }
          } else {
            startUpgradeFlow(data.triggerUser, data.triggerClientId);
          }
          break;

        case EventTriggerType.FlashStatus:
          if (isFlashStatusPayload(data.eventPayload)) {
            handleFlashStatusEvent(data.eventPayload, data.triggerUser, data.triggerClientId, data.triggerSource);
          }
          break;
      }
    },
    [
      location.pathname,
      displayArchivSystemRadialProgressModal,
      displayResetProgressModal,
      displayUpgradeRestartProgress,
      handleFlashStatusEvent,
      startUpgradeFlow,
      updateProgressFromEvent,
    ]
  );

  const closeSseEventTrigger = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  }, []);

  const startSseEventTrigger = useCallback(() => {
    if (!cbtApiUrl) return;
    if (unsubscribeRef.current) return;
    const baseUrl = cbtApiUrl.replace(/\/$/, '');
    const eventTriggerUrl = `${baseUrl}/api/sse/eventTrigger`;
    unsubscribeRef.current = sseConnectionManager.subscribe('EVENT_TRIGGER', eventTriggerUrl, handleEventTriggerData);
  }, [cbtApiUrl, handleEventTriggerData]);

  useEffect(() => {
    return () => closeSseEventTrigger();
  }, [closeSseEventTrigger]);

  return {
    startSseEventTrigger,
    closeSseEventTrigger,
  };
};
